import * as React from "react";
import { Header } from "./layout/Header.tsx";
import SelectionBox from "./boxes/Selection_Box.tsx";
import PredictionDisplay from "./PredictionDisplay.tsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.tsx";
import axios from "axios";

// API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.athenex.xyz";

interface Product {
  id: number;
  name: string;
  imageUrl: string;
}

export const Results: React.FC = () => {
  const [proteinTargets, setProteinTargets] = useState<Product[]>([]);
  const [compounds, setCompounds] = useState<Product[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [selectedCompound, setSelectedCompound] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const { isAuthenticated, isLoading, authError, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState<number>(0);
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch protein names from the backend API
  const fetchProteinNames = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dta/get_protein_names`, 
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.protein_names) {
        return response.data.protein_names;
      }
      return [];
    } catch (error) {
      console.error("Error fetching protein names:", error);
      setFetchError("Failed to fetch protein data");
      return [];
    }
  }, [getAuthHeaders]);

  // Fetch compound names from the backend API
  const fetchCompoundNames = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dta/get_compound_names`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.compound_names) {
        return response.data.compound_names;
      }
      return [];
    } catch (error) {
      console.error("Error fetching compound names:", error);
      setFetchError("Failed to fetch compound data");
      return [];
    }
  }, [getAuthHeaders]);

  // Fetch and refresh credits from the backend API
  const refreshCredits = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/dta/credits`,
        { headers: getAuthHeaders() }
      );
      if (response.data && typeof response.data.credits === 'number') {
        setCredits(response.data.credits);
      }
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const getProteinNames = async () => {
      try {
        const names = await fetchProteinNames();
        const proteinTargets = names.map((name: string, index: number) => ({
          id: index + 1,
          name,
          imageUrl: "", // Empty string as placeholder since we'll fetch it dynamically
        }));
        setProteinTargets(proteinTargets);
      } catch (error) {
        console.error("Error processing protein names:", error);
      }
    };

    const getCompoundNames = async () => {
      try {
        const names = await fetchCompoundNames();
        const compounds = names.map((name: string, index: number) => ({
          id: index + 1,
          name,
          imageUrl: "", // Empty string as placeholder since we'll fetch it dynamically
        }));
        setCompounds(compounds);
      } catch (error) {
        console.error("Error processing compound names:", error);
      }
    };

    // Only fetch data if authenticated
    if (isAuthenticated) {
      getProteinNames();
      getCompoundNames();
      refreshCredits();
      
      // Call login endpoint when component mounts - already handled by AuthContext
      console.log("Using authenticated session for Results page");
    }
  }, [isAuthenticated, fetchProteinNames, fetchCompoundNames, refreshCredits]);

  const handleTargetSelect = (product: Product | null) => {
    setSelectedTarget(product ? product.name : null);
  };

  const handleCompoundSelect = (product: Product | null) => {
    setSelectedCompound(product ? product.name : null);
  };

  // Show loading state while authentication is being checked
  if (isLoading) {
    return (
      <div className="flex overflow-hidden flex-col pb-6 bg-base min-h-screen">
        <Header />
        <hr className="border-t-0.5 border-black mt-2" />
        <div className="flex items-center justify-center h-full mt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p className="ml-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden flex-col pb-6 bg-base min-h-screen">
      <Header showCredits={true} credits={credits} />
      <hr className="border-t-0.5 border-black mt-2" />
      
      {authError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-4 my-2">
          <strong className="font-bold">Authentication Error: </strong>
          <span className="block sm:inline">{authError}</span>
          <button 
            className="ml-4 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
            onClick={() => navigate('/')}
          >
            Return to Login
          </button>
        </div>
      )}

      {fetchError && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mx-4 my-2">
          <strong className="font-bold">Data Error: </strong>
          <span className="block sm:inline">{fetchError}</span>
        </div>
      )}
      
      <div className="flex flex-grow p-4">
        <div className="w-1/6">
          <h2 className="text-2xl font-bold mb-2 text-center tracking-widest font-space-grotesk">
            Target
          </h2>
          <SelectionBox 
            products={proteinTargets} 
            type="protein" 
            onSelect={handleTargetSelect}
            apiBaseUrl={API_BASE_URL}
            apiHeaders={getAuthHeaders()}
          />
        </div>

        <div className="w-4/6 flex justify-center items-center">
          <PredictionDisplay 
            selectedTarget={selectedTarget}
            selectedDrug={selectedCompound}
            apiBaseUrl={API_BASE_URL}
            apiHeaders={getAuthHeaders()}
            onCreditsUpdate={refreshCredits}  // Changed from onPredictionComplete
          />
        </div>

        <div className="w-1/6">
          <h2 className="text-2xl font-bold mb-2 text-center tracking-widest font-space-grotesk">
            Compound
          </h2>
          <SelectionBox 
            products={compounds} 
            type="compound"
            onSelect={handleCompoundSelect}
            apiBaseUrl={API_BASE_URL}
            apiHeaders={getAuthHeaders()}
          />
        </div>
      </div>
    </div>
  );
};

export default Results;
